import { Fragment, useCallback, useEffect, useState } from 'react'
import * as cm from '@/services/contactMessagesService'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { SkeletonListPage } from '@/components/ui/Skeleton'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'

function notifyContactInboxChanged() {
  window.dispatchEvent(new CustomEvent('shopora-contact-inbox'))
}

const DEFAULT_LIMIT = 25

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

export default function ContactMessagesPage() {
  const { currentStoreId: storeId } = useCurrentStore()
  const [items, setItems] = useState<cm.ContactMessageRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(DEFAULT_LIMIT)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  useEffect(() => {
    setPage(1)
    setExpandedId(null)
  }, [storeId])

  const load = useCallback(async () => {
    if (storeId == null) return
    setLoading(true)
    setError(null)
    try {
      const data = await cm.fetchContactMessages(storeId, { page, limit })
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load messages'))
    } finally {
      setLoading(false)
    }
  }, [storeId, page, limit])

  useEffect(() => {
    void load()
  }, [load])

  async function openMessage(row: cm.ContactMessageRow) {
    setExpandedId((prev) => (prev === row.id ? null : row.id))
    if (!row.readAt && storeId != null) {
      try {
        await cm.markContactMessageRead(storeId, row.id)
        setItems((prev) =>
          prev.map((m) =>
            m.id === row.id ? { ...m, readAt: new Date().toISOString() } : m,
          ),
        )
        notifyContactInboxChanged()
      } catch {
        /* still show body */
      }
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1)

  return (
    <Card>
      <CardHeader
        title="Contact messages"
        description="Messages shoppers sent from your storefront contact form."
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading || !storeId}>
          Refresh
        </Button>
      </div>

      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}

      {storeId == null ? (
        <p className="text-sm text-muted-foreground">No store found for your organisation.</p>
      ) : loading ? (
        <SkeletonListPage columns={4} rows={6} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">From</th>
                  <th className="px-4 py-3 font-medium">Preview</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {items.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      className="cursor-pointer hover:bg-muted/20"
                      onClick={() => void openMessage(row)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-xs">{formatWhen(row.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{row.senderName}</div>
                        <div className="text-xs text-muted-foreground">{row.senderEmail}</div>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-muted-foreground">{row.body}</td>
                      <td className="px-4 py-3">
                        {row.readAt ? (
                          <span className="text-xs text-muted-foreground">Read</span>
                        ) : (
                          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                            New
                          </span>
                        )}
                      </td>
                    </tr>
                    {expandedId === row.id ? (
                      <tr className="bg-muted/10">
                        <td colSpan={4} className="px-4 py-4 text-foreground">
                          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Full message
                          </p>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed">{row.body}</p>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      No messages yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {total > 0 ? (
            <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Page <span className="font-medium text-foreground">{page}</span> of{' '}
                <span className="font-medium text-foreground">{totalPages}</span> — {total} total
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Card>
  )
}
